import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TrainerResponse } from '../../@shared/models/response.module';
import { TRN_16 } from '../../@shared/models/input.module';
import { TrainerService } from '../../@shared/services/trainer.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import { ToastService } from '../../@core/services/toast.service';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-trainers',
  standalone: false,
  templateUrl: './trainers.html',
  styleUrl: './trainers.css'
})
export class Trainers implements OnInit {
  trainers: TrainerResponse[] = [];
  filteredTrainers: TrainerResponse[] = [];

  // Filters
  searchQuery: string = '';
  selectedSpecialization: string = '';
  sortByDistance: boolean = false;

  // User location
  userLocation: { lat: number; lng: number } | null = null;
  locationLoading: boolean = false;

  // Trainer coordinates cache (from geocoding)
  trainerCoordinates: { [trainerId: string]: { lat: number; lng: number } } = {};

  // Pagination
  currentPage: number = 1;
  perPage: number = 12;
  totalPages: number = 1;
  totalItems: number = 0;

  // Loading state
  loading: boolean = false;

  // Map properties
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: '400px',
    mobileHeight: '300px',
    enableClustering: true,
    defaultCenter: { lat: 59.9139, lng: 10.7522 }, // Oslo, Norway
    defaultZoom: 5
  };
  geocodingInProgress: boolean = false;

  // Common specializations for filter dropdown (will be populated with translations)
  specializations: { label: string; value: string }[] = [];

  // Recommendation modal
  showRecommendModal: boolean = false;
  submittingRecommendation: boolean = false;
  recommendForm: TRN_16 = {
    trainer_name: '',
    description: '',
    specializations: '',
    contact_info: ''
  };

  // Predefined specializations - matches event disciplines
  availableSpecializations: string[] = [
    'dressage', 'jumping', 'eventing', 'driving', 'vaulting',
    'distance', 'icelandic', 'western', 'para_dressage',
    'mounted_games', 'working_eq', 'polo', 'other'
  ];

  constructor(
    private trainerService: TrainerService,
    private geocodingService: GeocodingService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  private readFiltersFromUrl() {
    const params = this.route.snapshot.queryParams;
    if (params['search']) this.searchQuery = params['search'];
    if (params['specialization']) this.selectedSpecialization = params['specialization'];
    if (params['sortByDistance'] === 'true') this.sortByDistance = true;
    if (params['page']) this.currentPage = +params['page'];
  }

  private updateUrlWithFilters() {
    const queryParams: any = {};
    if (this.searchQuery) queryParams.search = this.searchQuery;
    if (this.selectedSpecialization) queryParams.specialization = this.selectedSpecialization;
    if (this.sortByDistance) queryParams.sortByDistance = 'true';
    if (this.currentPage > 1) queryParams.page = this.currentPage;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  ngOnInit() {
    this.readFiltersFromUrl();
    this.initSpecializations();
    this.loadTrainers();
    this.getUserLocation();

    // Re-initialize specializations when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.initSpecializations();
    });
  }

  private initSpecializations() {
    this.specializations = [
      { label: this.translateService.instant('trainers.allSpecializations'), value: '' },
      ...this.availableSpecializations.map(spec => ({
        label: this.translateService.instant(`trainers.spec.${spec}`),
        value: spec
      }))
    ];
  }

  loadTrainers() {
    this.loading = true;
    const params = {
      page: this.currentPage,
      per_page: this.perPage,
      search: this.searchQuery || undefined,
      specialization: this.selectedSpecialization || undefined
    };

    this.trainerService.getTrainers(params).subscribe({
      next: (response) => {
        this.trainers = response.data || [];
        this.filteredTrainers = this.trainers;
        this.totalItems = response.total_items || 0;
        this.totalPages = response.total_pages || 1;
        this.loading = false;
        // Geocode trainers for map
        this.geocodeTrainers();
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  async geocodeTrainers() {
    this.geocodingInProgress = true;
    this.mapMarkers = [];

    const trainersToGeocode = this.filteredTrainers.filter(
      trainer => trainer.address || trainer.city || trainer.state || trainer.country
    );

    // Debug: log first trainer to check location fields
    if (trainersToGeocode.length > 0) {
      const first = trainersToGeocode[0];
      console.log('[Geocode Debug] First trainer location fields:', {
        full_name: first.full_name,
        address: first.address,
        postal_code: first.postal_code,
        city: first.city,
        state: first.state,
        country: first.country
      });
    }

    for (const trainer of trainersToGeocode) {
      const result = await this.geocodingService.geocodeAddress(
        trainer.address || '',
        trainer.city || '',
        trainer.postal_code || '',
        trainer.country || ''
      );

      if (result.success && result.lat && result.lng) {
        // Cache coordinates for distance calculation
        this.trainerCoordinates[trainer.user_id] = { lat: result.lat, lng: result.lng };

        this.mapMarkers = [...this.mapMarkers, {
          id: trainer.user_id,
          lat: result.lat,
          lng: result.lng,
          title: trainer.full_name || 'Unknown Trainer',
          subtitle: this.getLocation(trainer),
          imageUrl: trainer.profile_image_url || undefined,
          data: trainer
        }];
      }
    }

    this.geocodingInProgress = false;

    // Re-sort if sorting by distance is enabled
    if (this.sortByDistance && this.userLocation) {
      this.sortTrainersByDistance();
    }
  }

  private sortTrainersByDistance() {
    if (!this.userLocation) return;

    this.filteredTrainers.sort((a, b) => {
      const distA = this.getTrainerDistance(a);
      const distB = this.getTrainerDistance(b);
      if (distA === null && distB === null) return 0;
      if (distA === null) return 1;
      if (distB === null) return -1;
      return distA - distB;
    });
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    this.locationLoading = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.locationLoading = false;
      },
      () => {
        this.locationLoading = false;
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  toggleSortByDistance() {
    this.sortByDistance = !this.sortByDistance;
    if (this.sortByDistance && this.userLocation) {
      this.sortTrainersByDistance();
    } else {
      // Reset to original order by reloading
      this.filteredTrainers = [...this.trainers];
    }
    this.updateUrlWithFilters();
  }

  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getTrainerDistance(trainer: TrainerResponse): number | null {
    if (!this.userLocation) return null;
    const coords = this.trainerCoordinates[trainer.user_id];
    if (!coords) return null;
    return this.calculateDistance(
      this.userLocation.lat,
      this.userLocation.lng,
      coords.lat,
      coords.lng
    );
  }

  formatDistance(km: number | null): string {
    if (km === null) return '';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${Math.round(km)} km`;
  }

  onMapMarkerClick(marker: MapMarker) {
    const element = document.getElementById(`trainer-card-${marker.id}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight');
      setTimeout(() => element.classList.remove('highlight'), 2000);
    }
  }

  onMapInfoWindowAction(marker: MapMarker) {
    this.router.navigate(['/trainers', marker.id]);
  }

  onSearchChange() {
    this.currentPage = 1;
    this.loadTrainers();
    this.updateUrlWithFilters();
  }

  onSpecializationChange() {
    this.currentPage = 1;
    this.loadTrainers();
    this.updateUrlWithFilters();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedSpecialization = '';
    this.sortByDistance = false;
    this.currentPage = 1;
    this.loadTrainers();
    this.updateUrlWithFilters();
  }

  viewTrainer(trainer: TrainerResponse) {
    this.router.navigate(['/trainers', trainer.user_id]);
  }

  getInitials(trainer: TrainerResponse): string {
    const first = trainer.first_name?.charAt(0) || '';
    const last = trainer.last_name?.charAt(0) || '';
    return (first + last).toUpperCase();
  }

  getSpecializationsList(trainer: TrainerResponse): string[] {
    if (!trainer.trainer_specializations) return [];
    return trainer.trainer_specializations.split(',').map(s => s.trim()).filter(s => s);
  }

  translateSpecialization(spec: string): string {
    const oldToKey: { [key: string]: string } = {
      'Dressage': 'dressage', 'Jumping': 'jumping', 'Western': 'western',
      'Eventing': 'eventing', 'Reining': 'western', 'Trail Riding': 'distance',
      'Barrel Racing': 'western', 'Pole Bending': 'western',
      'Horsemanship': 'other', 'Ground Work': 'other', 'Liberty Training': 'other'
    };
    const key = oldToKey[spec] || spec;
    const i18nKey = `trainers.spec.${key}`;
    const translated = this.translateService.instant(i18nKey);
    return translated !== i18nKey ? translated : spec;
  }

  getLocation(trainer: TrainerResponse): string {
    const parts = [trainer.city, trainer.country].filter(p => p);
    return parts.join(', ');
  }

  // Pagination methods
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTrainers();
      this.updateUrlWithFilters();
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadTrainers();
      this.updateUrlWithFilters();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadTrainers();
      this.updateUrlWithFilters();
    }
  }

  // ==================== RECOMMENDATION METHODS ====================

  openRecommendModal() {
    this.showRecommendModal = true;
  }

  closeRecommendModal() {
    this.showRecommendModal = false;
  }

  submitRecommendation() {
    if (!this.recommendForm.trainer_name?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.nameRequired'));
      return;
    }
    if (!this.recommendForm.description?.trim()) {
      this.toastService.error(this.translateService.instant('trainer.reasonRequired'));
      return;
    }

    this.submittingRecommendation = true;
    this.trainerService.submitRecommendation(this.recommendForm).subscribe({
      next: () => {
        this.toastService.success(this.translateService.instant('trainer.recommendationSubmitted'));
        this.showRecommendModal = false;
        this.submittingRecommendation = false;
        this.recommendForm = {
          trainer_name: '',
          description: '',
          specializations: '',
          contact_info: ''
        };
      },
      error: () => {
        this.submittingRecommendation = false;
      }
    });
  }

  toggleRecommendSpecialization(spec: string) {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    const index = currentSpecs.indexOf(spec);
    if (index >= 0) {
      currentSpecs.splice(index, 1);
    } else {
      currentSpecs.push(spec);
    }
    this.recommendForm.specializations = currentSpecs.join(', ');
  }

  isRecommendSpecializationSelected(spec: string): boolean {
    const currentSpecs = (this.recommendForm.specializations || '')
      .split(',').map(s => s.trim()).filter(s => s);
    return currentSpecs.includes(spec);
  }
}
